{/* Copyright 2026 Adobe. All rights reserved.
This file is licensed to you under the Apache License, Version 2.0 (the "License");
you may not use this file except in compliance with the License. You may obtain a copy
of the License at http://www.apache.org/licenses/LICENSE-2.0
Unless required by applicable law or agreed to in writing, software distributed under
the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
OF ANY KIND, either express or implied. See the License for the specific language
governing permissions and limitations under the License. */}

import {Layout} from '../../../src/Layout';
export default Layout;

import vanillaDocs from 'docs:vanilla-starter/Sheet';
import React from 'react';
import ViewportDiagram from './SheetViewport.svg';
import SnapPointsDiagram from './SheetSnapPoints.svg';
import PreventDismissalDiagram from './SheetPreventDismissal.svg';
import StackingDiagram from './SheetStacking.svg';
import {SheetTimelineDemo} from './SheetTimelineDemo';

export const hideNav = true;
export const tags = ['sheet', 'drawer', 'tray', 'swipe', 'touch', 'gesture', 'component', 'accessibility', 'react aria', 'react'];
export const description = 'How we built a Sheet component with native swipe gestures, gesture-synchronized animations, snap points, and stacking, entirely powered by CSS scroll snapping and view timelines.';
export const date = '2026-10-08';
export const author = 'Devon Govett';
export const authorLink = 'https://x.com/devongovett';
export const section = 'Blog';
export const isSubpage = true;

# Scrolling is All You Need: Building a Swipeable Sheet

We're excited to introduce the React Aria [Sheet](react-aria:Sheet) component! A Sheet is an overlay that slides in from an edge of the viewport, and can be swiped by the user to dismiss. It supports gesture-synchronized animations, snap points, stacking, overscroll padding, software keyboard awareness, and multiple placements and swipe directions.

```tsx render hideCode docs={vanillaDocs.exports.Sheet} links={vanillaDocs.links} props={['position', 'swipeDirection', 'preventDismissal', 'overscrollPadding']} type="vanilla"
"use client";
import {useState} from 'react';
import {SheetTrigger} from 'react-aria-components/Sheet';
import {Sheet, Heading} from 'vanilla-starter/Sheet';
import {Button} from 'vanilla-starter/Button';
import {Minus, Plus, ShoppingCart} from 'lucide-react';

function Example(props) {
  let [items, setItems] = useState([
    {name: 'Pork Bao', detail: 'Steamed · 2 pieces', emoji: '🥟', price: 4.5, qty: 2},
    {name: 'Spicy Ramen', detail: 'Extra spicy · Soft egg', emoji: '🍜', price: 13, qty: 1},
    {name: 'Mango Boba', detail: 'Less ice · 50% sweet', emoji: '🧋', price: 5.75, qty: 1}
  ]);
  let update = (name, delta) => setItems(items => items
    .map(item => item.name === name ? {...item, qty: item.qty + delta} : item)
    .filter(item => item.qty > 0));
  let subtotal = items.reduce((sum, item) => sum + item.price * item.qty, 0);
  let delivery = items.length ? 2.99 : 0;

  return (
    <SheetTrigger>
      <Button>
        <ShoppingCart size={18} />
        <span>Cart ({items.reduce((n, item) => n + item.qty, 0)})</span>
      </Button>
      {/*- begin highlight -*/}
      <Sheet {...props}/* PROPS */>
      {/*- end highlight -*/}
        {/*- begin collapse -*/}
        <div style={{display: 'flex', flexDirection: 'column', minHeight: '100%'}}>
          <Heading slot="title">Your order</Heading>
          <p style={{margin: '0 0 20px', color: 'var(--gray-1000)'}}>Delivering from Bao Bros · 25–35 min</p>
          <div style={{display: 'flex', flexDirection: 'column', gap: 'var(--spacing-4)', flexGrow: 1}}>
            {items.map(item => (
              <div key={item.name} style={{display: 'flex', alignItems: 'center', gap: 'var(--spacing-3)'}}>
                <span style={{width: 'calc(15 * var(--spacing))', height: 'calc(15 * var(--spacing))', borderRadius: 12, background: 'var(--gray-200)', fontSize: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0}}>{item.emoji}</span>
                <div style={{display: 'flex', alignItems: 'center', columnGap: 'var(--spacing-3)', rowGap: 4, flexWrap: 'wrap', flexGrow: 1}}>
                  <div style={{flexGrow: 100, minWidth: 0}}>
                    <div style={{fontWeight: 600}}>{item.name}</div>
                    <div style={{fontSize: 12, color: 'var(--gray-1000)'}}>{item.detail}</div>
                  </div>
                  <div style={{display: 'flex', alignItems: 'center', gap: 4, flexGrow: 1}}>
                    <Button aria-label={`Remove one ${item.name}`} variant="secondary" onPress={() => update(item.name, -1)}><Minus size={16} /></Button>
                    <span style={{minWidth: 20, textAlign: 'center'}}>{item.qty}</span>
                    <Button aria-label={`Add one ${item.name}`} variant="secondary" onPress={() => update(item.name, 1)}><Plus size={16} /></Button>
                    <span style={{minWidth: '6ch', textAlign: 'end', flexGrow: 1}}>${(item.price * item.qty).toFixed(2)}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div style={{display: 'flex', flexDirection: 'column', gap: 4, margin: '20px 0', paddingTop: 16, borderTop: '1px solid var(--gray-300)'}}>
            <div style={{display: 'flex', justifyContent: 'space-between'}}><span>Subtotal</span><span>${subtotal.toFixed(2)}</span></div>
            <div style={{display: 'flex', justifyContent: 'space-between'}}><span>Delivery fee</span><span>${delivery.toFixed(2)}</span></div>
            <div style={{display: 'flex', justifyContent: 'space-between', fontWeight: 600}}><span>Total</span><span>${(subtotal + delivery).toFixed(2)}</span></div>
          </div>
          <Button slot="close" style={{width: '100%'}}>Place order</Button>
        </div>
        {/*- end collapse -*/}
      </Sheet>
    </SheetTrigger>
  );
}
```

Our implementation is entirely built using native CSS scroll snapping and view timelines, on top of our existing [Modal](react-aria:Modal) component. This means swipe gestures run off the main thread at the native display refresh rate without any JavaScript touch handling, and we get fluid momentum scrolling and elastic physics for free. In this post, I'll explain how it works.

## Setting up the viewport

To create the illusion of swiping to open and close a sheet, we use regular CSS overflow scrolling. The sheet overlay is a scrollable element positioned above the rest of the page, hiding its scrollbar with [`scrollbar-width: none`](https://developer.mozilla.org/en-US/docs/Web/CSS/Reference/Properties/scrollbar-width). It is double the height of the window viewport, and positioned so that only the top half is visible, with the sheet in the bottom half. The sheet is inside a "stage", which is a viewport-sized flex container that positions the sheet itself on the edge of the viewport (e.g. the bottom). We add [CSS scroll snap](https://developer.mozilla.org/en-US/docs/Web/CSS/Guides/Scroll_snap) points at the top and bottom of the sheet so that it always stops either fully open or fully closed.

<ViewportDiagram role="img" aria-label="Diagram of the sheet scroller in its closed and open states. The scroller is twice the height of the window, containing an exit snap point, the stage with the sheet, and another exit snap point. When closed, scrollTop is 0 and the window shows the empty exit snap point while the stage is clipped below. When open, the scroller is scrolled to its maximum and the window shows the stage with the sheet." style={{width: '100%', maxWidth: 849, height: 'auto', display: 'block', margin: '20px auto'}} />

When the sheet is closed, the scroll position is zero, so the sheet is off screen (clipped by the window viewport). When it opens, it scrolls down to 100% with [`scroll-behavior: smooth`](https://developer.mozilla.org/en-US/docs/Web/CSS/Reference/Properties/scroll-behavior), which animates the sheet up and into view. The user can swipe it away by scrolling to the top (moving the sheet down). Once we detect that the sheet is completely out of view (via an [`IntersectionObserver`](https://developer.mozilla.org/en-US/docs/Web/API/Intersection_Observer_API)), it is unmounted.

To account for native rubber band scrolling physics, the `overscrollPadding` prop adds additional padding to the edge of the sheet so that it appears to stretch beyond the viewport when dragged.

All of this geometry flips depending on the `swipeDirection` and `position` props, so it works for top, left, right, and center sheets as well.

## Gesture-synced animations

The backdrop and the sheet itself support a `swipeAnimation` prop, which is a CSS keyframe animation synchronized with the swipe gesture. For example, the backdrop might fade in and out, and the sheet could apply a transform or adjust its corner radius based on the swipe progress. This uses CSS [`view-timeline`](https://developer.mozilla.org/en-US/docs/Web/CSS/Reference/Properties/view-timeline), which tracks how much of an element is visible and uses this to drive a keyframe animation. We use [`view-timeline-inset: 0 100dvh`](https://developer.mozilla.org/en-US/docs/Web/CSS/Reference/Properties/view-timeline-inset) so that swipe animations begin when the sheet is visible, not when clipped by the window viewport. Swipe the demo below to see how it works.

<SheetTimelineDemo />

## Snap points

Sheet supports custom `snapPoints`, which allow it to stop partially open at specific visible heights. This is implemented by rendering an invisible 1px absolutely positioned marker div inside the sheet for each snap point. [`scroll-margin-top: 100dvh`](https://developer.mozilla.org/en-US/docs/Web/CSS/Reference/Properties/scroll-margin-top) offsets the scroll position so the marker snaps to the bottom of the viewport, leaving the specified height of the sheet on screen.

<SnapPointsDiagram role="img" aria-label="Diagram of a snap point. A 1×1 marker is positioned inside the sheet, p from its top edge, where p can be a percentage of the sheet size S. With scroll-margin-top: 100dvh and scroll-snap-align: start, the marker snaps to the bottom edge of the window, leaving p of the sheet visible." style={{width: '100%', maxWidth: 500, height: 'auto', display: 'block', margin: '20px auto'}} />

When a sheet is partially visible, we disable scrolling the sheet's inner content by setting `overflow: hidden`. This allows the user to swipe anywhere to expand. Once the sheet is fully open, the content becomes scrollable. [`overscroll-behavior: auto`](https://developer.mozilla.org/en-US/docs/Web/CSS/Reference/Properties/overscroll-behavior) enables scroll chaining, which means that when the user is at the top of the inner content, they can swipe down to dismiss the sheet. When they have scrolled the inner content, swiping down scrolls the content.

```tsx render hideCode
"use client";
import {SheetTrigger} from 'react-aria-components/Sheet';
import {Sheet, Heading} from 'vanilla-starter/Sheet';
import {Button} from 'vanilla-starter/Button';
import {Star, MapPin, Clock, Phone} from 'lucide-react';
import {Map} from '../Map';

function Example() {
  let reviews = [
    {name: 'Maya R.', date: '2 days ago', text: 'The espresso was smooth and the staff was lovely. A great spot for a quiet morning.'},
    {name: 'Jordan K.', date: '1 week ago', text: 'Really good pour-over and plenty of seating. It gets busy around lunch, but the line moves quickly.'},
    {name: 'Avery L.', date: '2 weeks ago', text: 'The iced coffee was bright and refreshing, and the pastries were excellent. I will definitely be back.'},
    {name: 'Chris T.', date: '1 month ago', text: 'Beautiful space with consistently good coffee. The cappuccino had the perfect amount of foam.'},
    {name: 'Samira P.', date: '1 month ago', text: 'Friendly service, fast Wi-Fi, and a great neighborhood atmosphere. One of my favorite coffee shops in Port Marlow.'}
  ];

  return (
    <SheetTrigger>
      <Button>Show Sheet with Snap Points</Button>
      {/*- begin highlight -*/}
      <Sheet position="bottom" snapPoints={[280]}>
      {/*- end highlight -*/}
        <Heading slot="title" style={{marginBottom: 4}}>Harborlight Coffee</Heading>
        <div style={{display: 'flex', alignItems: 'center', gap: 8, color: 'var(--gray-1000)', marginBottom: 32}}>
          <Star size={16} fill="currentColor" style={{color: '#ff9500'}} />
          <strong>4.6</strong> · Coffee shop · $$
        </div>
        <div style={{display: 'flex', flexDirection: 'column', gap: 16, alignItems: 'start'}}>
          <div style={{display: 'flex', gap: 12, alignItems: 'center'}}><MapPin size={18} /> 300 Harbor St, Port Marlow</div>
          <div style={{display: 'flex', gap: 12, alignItems: 'center'}}><Clock size={18} /> Open until 6:00 PM</div>
          <div style={{display: 'flex', gap: 12, alignItems: 'center'}}><Phone size={18} /> (510) 555-0142</div>
          <p>Drag this sheet up to see more, or swipe down to dismiss. Snap points let you preview the most relevant content first, then expand for the full story.</p>
          <Map />
          <Button slot="close">Get directions</Button>
          <h3 style={{margin: '8px 0 0'}}>Reviews</h3>
          <div style={{display: 'flex', flexDirection: 'column', gap: 16, width: '100%'}}>
            {reviews.map(review => (
              <div key={review.name} style={{padding: 16, border: '1px solid var(--gray-300)', borderRadius: 12}}>
                <div style={{display: 'flex', justifyContent: 'space-between', gap: 8, marginBottom: 8}}>
                  <strong>{review.name}</strong>
                  <span style={{color: 'var(--gray-700)', fontSize: 14}}>{review.date}</span>
                </div>
                <div style={{display: 'flex', gap: 2, color: '#ff9500', marginBottom: 8}}>
                  {Array.from({length: 5}, (_, i) => <Star key={i} size={14} fill="currentColor" />)}
                </div>
                <div>{review.text}</div>
              </div>
            ))}
          </div>
        </div>
      </Sheet>
    </SheetTrigger>
  );
}
```

## Non-dismissible sheets

When the `preventDismissal` prop is true, the user cannot swipe the sheet away. However, they can still swipe between snap points. In this case, after the entering animation, the stage is adjusted so that the bottom of the viewport aligns with the first snap point at `scrollTop = 0`. This prevents it from being swiped off the screen, and preserves the native feeling scroll bounce.

<PreventDismissalDiagram role="img" aria-label="Diagram comparing a dismissible sheet with a sheet using preventDismissal. In a dismissible sheet, scrollTop 0 shows the exit snap point and dismisses the sheet. With preventDismissal, the stage moves up by T, the distance from the smallest snap point marker to the bottom of the stage, so scrollTop 0 leaves the smallest snap point visible." style={{width: '100%', maxWidth: 863, height: 'auto', display: 'block', margin: '20px auto'}} />

```tsx render hideCode
"use client";
import {SheetTrigger} from 'react-aria-components/Sheet';
import {Sheet, Heading} from 'vanilla-starter/Sheet';
import {Button} from 'vanilla-starter/Button';
import {Star, MapPin, Clock, Phone, X} from 'lucide-react';
import {Map} from '../Map';

function Example() {
  return (
    <SheetTrigger>
      <Button>Open Non-Dismissible Sheet</Button>
      {/*- begin highlight -*/}
      <Sheet position="bottom" snapPoints={[280]} preventDismissal>
      {/*- end highlight -*/}
        <Heading slot="title" style={{marginBottom: 4}}>Harborlight Coffee</Heading>
        <Button slot="close" variant="secondary" aria-label="Close" style={{position: 'absolute', top: 24, right: 24}}>
          <X />
        </Button>
        <div style={{display: 'flex', alignItems: 'center', gap: 8, color: 'var(--gray-1000)', marginBottom: 32}}>
          <Star size={16} fill="currentColor" style={{color: '#ff9500'}} />
          <strong>4.6</strong> · Coffee shop · $$
        </div>
        <div style={{display: 'flex', flexDirection: 'column', gap: 16, alignItems: 'start'}}>
          <div style={{display: 'flex', gap: 12, alignItems: 'center'}}><MapPin size={18} /> 300 Harbor St, Port Marlow</div>
          <div style={{display: 'flex', gap: 12, alignItems: 'center'}}><Clock size={18} /> Open until 6:00 PM</div>
          <div style={{display: 'flex', gap: 12, alignItems: 'center'}}><Phone size={18} /> (510) 555-0142</div>
          <p>Drag this sheet up to see more, or swipe down to collapse. Snap points let you preview the most relevant content first, then expand for the full story. Use the close button to dismiss.</p>
          <Map />
        </div>
      </Sheet>
    </SheetTrigger>
  );
}
```

## Stacking

Sheets can be stacked with a custom `stackAnimation`, driven by the same view timelines that track the swipe gesture. These are defined on the root `html` element via [`timeline-scope`](https://developer.mozilla.org/en-US/docs/Web/CSS/Reference/Properties/timeline-scope), which allows them to be used by any element on the page. One or more instances of the stack animation are applied to each sheet, tied to the timelines of the sheets in front of it. [`animation-composition: accumulate`](https://developer.mozilla.org/en-US/docs/Web/CSS/Reference/Properties/animation-composition) combines their effects together, so for each sheet the stack depth appears to increase.

<StackingDiagram role="img" aria-label="Diagram of three stacked sheets. Each sheet defines a view timeline hoisted to the html element with timeline-scope. Sheet 0 runs the stack animation twice, driven by the timelines of sheets 1 and 2 with animation-composition: accumulate, so it appears two levels deep. Sheet 1 runs it once, driven by sheet 2. Sheet 2 is in front and has no stack animation." style={{width: '100%', maxWidth: 646, height: 'auto', display: 'block', margin: '20px auto'}} />

```tsx render hideCode
"use client";
import {SheetTrigger} from 'react-aria-components/Sheet';
import {Sheet, Heading} from 'vanilla-starter/Sheet';
import {Button} from 'vanilla-starter/Button';
import {Form} from 'vanilla-starter/Form';
import {TextField} from 'vanilla-starter/TextField';
import {RadioGroup, Radio} from 'vanilla-starter/RadioGroup';
import {CheckboxGroup} from 'vanilla-starter/CheckboxGroup';
import {Checkbox} from 'vanilla-starter/Checkbox';
import {ChevronRight} from 'lucide-react';

function Example() {
  return (
    <SheetTrigger>
      <Button>Open Sheet Stack</Button>
      <Sheet style={{height: 550}}>
        <Heading slot="title">Settings</Heading>
        <p style={{marginTop: 0, marginBottom: 32}}>Manage your account and preferences.</p>
        <Form>
          <TextField label="Homepage" defaultValue="react-aria.adobe.com" />
          <RadioGroup label="Theme" defaultValue="auto">
            <Radio value="auto">Auto</Radio>
            <Radio value="light">Light</Radio>
            <Radio value="dark">Dark</Radio>
          </RadioGroup>
          <SheetTrigger>
            <Button variant="secondary">
              <span>Account</span>
              <ChevronRight size={18} />
            </Button>
            <Sheet style={{height: 550}}>
              <Heading slot="title">Account</Heading>
              <p style={{marginTop: 0, marginBottom: 32}}>Swipe this sheet away to return to Settings.</p>
              <Form>
                <TextField label="Name" defaultValue="Devon Govett" />
                <TextField label="Username" defaultValue="@devongovett" />
                <TextField label="Email" defaultValue="devon@example.com" />
                <div style={{display: 'flex', justifyContent: 'space-between'}}>
                  <Button slot="close">Save</Button>
                  <SheetTrigger>
                    <Button variant="secondary">
                      <span>Advanced</span>
                      <ChevronRight size={18} />
                    </Button>
                    <Sheet style={{height: 550}}>
                      <Heading slot="title">Advanced</Heading>
                      <p style={{marginTop: 0, marginBottom: 32}}>Swipe this sheet away to return to Account.</p>
                      <Form>
                        <CheckboxGroup label="Notification settings" defaultValue={['account', 'dms']}>
                          <Checkbox value="account">Account activity</Checkbox>
                          <Checkbox value="mentions">Mentions</Checkbox>
                          <Checkbox value="dms">Direct message</Checkbox>
                          <Checkbox value="marketing">Marketing emails</Checkbox>
                        </CheckboxGroup>
                        <Button slot="close">Save</Button>
                      </Form>
                    </Sheet>
                  </SheetTrigger>
                </div>
              </Form>
            </Sheet>
          </SheetTrigger>
        </Form>
      </Sheet>
    </SheetTrigger>
  );
}
```

## Software keyboards

The software keyboard, especially on iOS, is notoriously difficult to wrangle. It appears on top of content rather than pushing it, and often causes the entire page to scroll when it opens. The dynamic viewport units also do not adjust when the keyboard opens. We account for this by applying `padding-bottom: calc(100dvh - var(--visual-viewport-height))` to the sheet's scrollable content. This allows the user to reach the content at the bottom of the sheet, while it still visually scrolls behind the keyboard.

Thanks to [Nikolas Schröter](https://github.com/nwidynski), we also improved our `usePreventScroll` hook to reduce layout shifts that occur when the keyboard opens and closes. Tapping an input inside a Sheet opens the keyboard and smoothly scrolls the input into view, without scrolling the entire page.

## Wrap up

Modern CSS is surprisingly powerful. By combining scroll snapping, view timeline animations, overscroll-behavior, and dynamic viewport units, we've been able to build a sheet component with fully native swipe gestures and fluid animations. This would not have been possible just a few years ago.

Check out the React Aria [Sheet](react-aria:Sheet) component to learn how you can use it in your application or component library!
